import React from 'react';
import { Flex, Button, Icon } from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import LargeButton from '../../../../common/src/components/LargeButton';
import { mq } from '../../../../common/src/utils/styledComponentsUtils';
import AssistantText from './offers-assistant/AssistantText';
import { getTextForRiskObject } from '../../services/productFactoryService';
import { useHistory } from 'react-router';
import { useLouiseDispatch, useLouiseSelector } from '../../store/redux/store';
import { ProductsQualityRespWithPagination } from '../../../../../types/Quotes';
import DesktopOffersFilterBar from './offers-fiter-bar/DesktopOffersFilterBar';
import OffersRiskObjectErrors from './OffersRiskObjectErrors';
import { clearComparisonMatrix } from '../../store/redux/slices/offersSlice';
import useSessionId from '../../../../flows/src/hooks/useSessionId';
import { isSocialLoginUrl } from '../../../../app/src/features/authentication/constants/SocialAuth';

interface Props {
  riskObject: string;
  selectedGuarantees: string[];
  products: ProductsQualityRespWithPagination;
  comparisonQuotesAmount: number;
}

const OffersContainerHeader: React.FC<React.PropsWithChildren<Props>> = ({
  riskObject,
  selectedGuarantees,
  products,
  comparisonQuotesAmount,
}) => {
  const { t } = useTranslation();
  const sessionId = useSessionId();
  const history = useHistory();
  const dispatch = useLouiseDispatch();

  const selectedRiskObjects = useLouiseSelector(
    (state) => state.selection.selectedRiskObjects,
  );

  const hasOmnium =
    isSocialLoginUrl ||
    selectedGuarantees?.some((guarantee) =>
      guarantee.toLowerCase().match(/omnium/),
    );

  const sortedRiskObjects = [...selectedRiskObjects].sort();

  const nextRiskObject =
    sortedRiskObjects.sort()[
      sortedRiskObjects.findIndex((_riskObject) => _riskObject === riskObject) +
        1
    ];

  const riskObjectErrors = products?.risk_object_errors.filter(
    ({ insurance_type }) => insurance_type === riskObject,
  );

  const getNextButtonAction = () => {
    if (nextRiskObject)
      history.push(
        `/product/session/offers/overview/${nextRiskObject}?session_id=${sessionId}`,
      );
    else
      history.push(
        `/product/session/offers/selectedOffers?session_id=${sessionId}`,
      );
  };

  return (
    <>
      <Flex gridGap="4" justifyContent="space-between">
        <AssistantText
          title={t('Here are your x insurance offers', {
            riskType: t(riskObject).toLowerCase(),
          })}
          description={t('SALES-CONVERSATION_OFFERS_HEADER_DESCRIPTION', {
            riskType: t(riskObject).toLowerCase(),
          })}
        />
        <Button
          rightIcon={<Icon name="IconArrow" color="currentColor" />}
          flexShrink={0}
          borderRadius="full"
          onClick={getNextButtonAction}
          display={{ base: 'none', laptop: 'inherit' }}
          mt="4"
          variant="tertiary"
        >
          {nextRiskObject
            ? getTextForRiskObject(nextRiskObject)
            : t('Selected offers')}
        </Button>
      </Flex>
      {comparisonQuotesAmount > 0 && (
        <StyledLargeButton onClick={() => dispatch(clearComparisonMatrix())}>
          {t('Clear list of compared offers')}
        </StyledLargeButton>
      )}
      {['car', 'two_wheeler'].includes(riskObject) && hasOmnium && (
        <DesktopOffersFilterBar guarantees={selectedGuarantees} />
      )}
      {riskObjectErrors && riskObjectErrors.length > 0 && (
        <OffersRiskObjectErrors riskObjectErrors={riskObjectErrors} />
      )}
    </>
  );
};

const StyledLargeButton = styled(LargeButton)`
  margin-bottom: 1.6rem;
  width: 100%;

  ${mq.laptop} {
    display: none;
  }
`;

export default OffersContainerHeader;
